import React from 'react';
import PropTypes from 'prop-types';
import { DollarSign, Plus, Trash2 } from 'lucide-react';
import {
  CurrencyInput,
  FormSelect,
  SectionHeader,
  FormNavigation,
  FormRow,
} from '../FormComponents';

const incomeTypeOptions = [
  { value: 'base', label: 'Base Employment Income' },
  { value: 'overtime', label: 'Overtime' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'commission', label: 'Commission' },
  { value: 'self_employment', label: 'Self-Employment Income' },
  { value: 'rental', label: 'Rental Income' },
  { value: 'social_security', label: 'Social Security' },
  { value: 'pension', label: 'Pension' },
  { value: 'retirement', label: '401k/IRA Distributions' },
  { value: 'disability', label: 'Disability Income' },
  { value: 'child_support', label: 'Child Support Received' },
  { value: 'alimony', label: 'Alimony Received' },
  { value: 'investment', label: 'Dividend/Interest Income' },
  { value: 'other', label: 'Other Income' },
];

export default function Income({ data, onChange, onNext, onBack }) {
  const incomes = data.incomes || [];

  const addIncome = () => {
    onChange({
      ...data,
      incomes: [...incomes, { id: Date.now(), type: '', amount: '' }],
    });
  };

  const updateIncome = (id, field, value) => {
    onChange({
      ...data,
      incomes: incomes.map((inc) =>
        inc.id === id ? { ...inc, [field]: value } : inc
      ),
    });
  };

  const removeIncome = (id) => {
    onChange({
      ...data,
      incomes: incomes.filter((inc) => inc.id !== id),
    });
  };

  const totalMonthlyIncome = incomes.reduce(
    (sum, inc) => sum + (parseFloat(inc.amount) || 0),
    0
  );

  const isValid = incomes.length > 0 && incomes.every((inc) => Boolean(inc.type) && Boolean(inc.amount));

  return (
    <div>
      <SectionHeader
        title="Income Sources"
        subtitle="List all sources of monthly income"
        icon={<DollarSign size={28} />}
      />

      <div
        style={{
          padding: '16px 20px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(16, 185, 129, 0.05))',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ color: '#94a3b8', fontSize: '14px' }}>Total Monthly Income</span>
        <span
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#22c55e',
          }}
        >
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
          }).format(totalMonthlyIncome)}
        </span>
      </div>

      {incomes.length === 0 && (
        <div
          style={{
            padding: '32px',
            borderRadius: '12px',
            border: '2px dashed rgba(148, 163, 184, 0.3)',
            textAlign: 'center',
            marginBottom: '20px',
          }}
        >
          <p style={{ color: '#94a3b8', margin: '0 0 16px' }}>
            No income sources added yet. Click below to add your first income source.
          </p>
        </div>
      )}

      {incomes.map((income, index) => (
        <div
          key={income.id}
          style={{
            padding: '20px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            marginBottom: '16px',
            position: 'relative',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <span
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#64748b',
              }}
            >
              Income Source #{index + 1}
            </span>
            <button
              type="button"
              onClick={() => removeIncome(income.id)}
              style={{
                background: 'none',
                border: 'none',
                color: '#f87171',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
              }}
            >
              <Trash2 size={14} /> Remove
            </button>
          </div>

          <FormRow>
            <FormSelect
              label="Income Type"
              value={income.type}
              onChange={(value) => updateIncome(income.id, 'type', value)}
              options={incomeTypeOptions}
              placeholder="Select type"
              required
            />
            <CurrencyInput
              label="Monthly Amount"
              value={income.amount}
              onChange={(value) => updateIncome(income.id, 'amount', value)}
              required
              placeholder="$0"
            />
          </FormRow>
        </div>
      ))}

      <button
        type="button"
        onClick={addIncome}
        style={{
          width: '100%',
          padding: '14px',
          borderRadius: '12px',
          border: '2px dashed rgba(56, 189, 248, 0.4)',
          background: 'rgba(56, 189, 248, 0.05)',
          color: '#38bdf8',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
          marginBottom: '8px',
        }}
      >
        <Plus size={18} /> Add Income Source
      </button>

      <p style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', marginBottom: '24px' }}>
        Include all regular income sources. You may be asked to verify income with documentation.
      </p>

      <FormNavigation
        onBack={onBack}
        onNext={onNext}
        isValid={isValid}
      />
    </div>
  );
}

Income.propTypes = {
  data: PropTypes.shape({
    incomes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        amount: PropTypes.string,
      })
    ),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};
